import { onlineManager } from '@tanstack/react-query';
import type { DeliveryPartner, Partner, StoriesResponse, User, Verticale } from '@doorly/shared';

import { isRetail, type MyStoresResponse, type PinVerifier } from '@doorly/shared';
import {
  db,
  type CachedDeliveryPartnerRow,
  type CachedPartnerRow,
  type CachedUserRow,
  type CachedVerticaleRow,
  type CachedStoryRow,
  type PinVerifierRow,
} from '../../db/schema';
import { isActingAs } from '../../lib/actingAs';
import { apiClient } from '../../lib/apiClient';

type TokenProvider = () => Promise<string | null>;

const WARM_INTERVAL_MS = 5 * 60 * 1000; // 5 min

// Keeps Dexie warm with the reference data the app needs to stay functional
// offline (users for hierarchy lookups, partners for product picker,
// verticales for the wizard). Runs once on sign-in, then every 5 min while online.
// Failures are silent — stale cache is always better than no cache.
class CacheWarmerImpl {
  private timer: ReturnType<typeof setInterval> | null = null;
  private getToken: TokenProvider | null = null;

  configure(provider: TokenProvider) {
    this.getToken = provider;
  }

  start() {
    void this.warm();
    this.timer = setInterval(() => void this.warm(), WARM_INTERVAL_MS);
  }

  stop() {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
  }

  /**
   * On-demand re-warm. Call this after a config mutation (e.g. admin
   * toggling a verticale) so the current session picks up the change
   * without waiting for the 5 min interval.
   */
  async refresh(): Promise<void> {
    await this.warm();
  }

  /**
   * Réconcilie le cache des stories avec la réponse de l'API.
   *
   * Par FICHE (`leadId`), pas par `id` : l'API ne renvoie qu'une story par
   * fiche et son id `kind:leadId` change quand le dernier fait change (« à
   * prévenir » → « vente activée »). Or « à rappeler » est une intention du
   * vendeur : elle doit survivre à ce changement. `seen` et « c'est vu », eux,
   * ne survivent que si le fait n'a pas bougé (`at` identique) — une nouvelle
   * tentative du centre fait avancer `at` et rallume la story. Sans ça, un
   * vendeur prévenu mardi ne l'était plus jamais de la semaine.
   *
   * N'écrit que ce qui change : Dexie réveille les `liveQuery` (anneau, Fil)
   * sur toute écriture, même à valeur identique — vider/réinsérer à chaque
   * tick faisait re-rendre le Fil sous le doigt du vendeur.
   *
   * `'forbidden'` = flag fermé pour ce rôle → table vidée. `null` = erreur
   * réseau → on ne touche à rien.
   */
  private async warmStories(res: StoriesResponse | 'forbidden' | null): Promise<void> {
    if (res === null) return;
    await db.transaction('rw', [db.stories], async () => {
      if (res === 'forbidden') {
        await db.stories.clear();
        return;
      }
      const existing = await db.stories.toArray();
      const previous = new Map(existing.map((r) => [r.leadId, r] as const));
      const keep = new Set<string>();
      const puts: CachedStoryRow[] = [];
      for (const st of res.stories) {
        const prev = previous.get(st.leadId);
        const unchanged = !!prev && prev.at === st.at;
        const state =
          prev?.state === 'todo'
            ? 'todo'
            : prev?.state === 'dismissed' && unchanged
              ? 'dismissed'
              : undefined;
        const row: CachedStoryRow = {
          id: st.id,
          kind: st.kind,
          at: st.at,
          leadId: st.leadId,
          seen: prev && prev.seen === 1 && unchanged ? 1 : 0,
          ...(state ? { state } : {}),
          lead: st.lead,
          detail: st.detail,
        };
        keep.add(row.id);
        if (!prev || prev.id !== row.id || JSON.stringify(prev) !== JSON.stringify(row))
          puts.push(row);
      }
      // Couvre aussi les caches de la v1 (#444), qui portaient plusieurs
      // stories par fiche : tout id absent de la réponse disparaît.
      const stale = existing.map((r) => r.id).filter((id) => !keep.has(id));
      if (stale.length > 0) await db.stories.bulkDelete(stale);
      if (puts.length > 0) await db.stories.bulkPut(puts);
    });
  }

  private async warmPinVerifiers(res: PinVerifier[] | 'forbidden' | null): Promise<void> {
    if (res === null) return;
    await db.transaction('rw', [db.pinVerifiers], async () => {
      if (res === 'forbidden') {
        if ((await db.pinVerifiers.count()) > 0) await db.pinVerifiers.clear();
        return;
      }
      await db.pinVerifiers.clear();
      const rows: PinVerifierRow[] = res.map((v) => ({ userId: v.userId, verifier: v.verifier }));
      if (rows.length > 0) await db.pinVerifiers.bulkAdd(rows);
    });
  }

  /**
   * Magasins de l'appareil (niveau tablette). `null` = erreur réseau → cache
   * gardé : le bandeau doit continuer d'afficher le magasin hors ligne.
   */
  private async warmDeviceStores(res: MyStoresResponse | null): Promise<void> {
    if (res === null) return;
    const prev = await db.deviceStores.get('device');
    const next = {
      key: 'device' as const,
      deviceUserId: res.deviceUserId,
      retail: res.retail,
      stores: res.stores,
      networkHasStores: res.networkHasStores === true,
      fetchedAt: new Date().toISOString(),
    };
    // N'écrit que si ça change : chaque écriture réveille les liveQuery.
    if (
      prev &&
      prev.deviceUserId === next.deviceUserId &&
      prev.retail === next.retail &&
      prev.networkHasStores === next.networkHasStores &&
      JSON.stringify(prev.stores) === JSON.stringify(next.stores)
    ) {
      return;
    }
    await db.deviceStores.put(next);
  }

  /** Re-lecture immédiate des magasins de l'appareil (après une affectation). */
  async refreshDeviceStores(): Promise<void> {
    if (!this.getToken || !onlineManager.isOnline()) return;
    const token = await this.getToken();
    if (!token) return;
    const res = await apiClient
      .get<MyStoresResponse>('/users/me/stores', token, { actingAs: null })
      .catch(() => null);
    await this.warmDeviceStores(res);
  }

  private async warm(): Promise<void> {
    if (!this.getToken || !onlineManager.isOnline()) return;
    try {
      const token = await this.getToken();
      if (!token) return;
      // Les référentiels sont lus au NIVEAU TABLETTE (`actingAs: null`) : en
      // session PIN, la liste des vendeurs du magasin — celle de la modale
      // « Changer d'utilisateur » — est celle que voit le superviseur, pas le
      // vendeur actif (qui ne se verrait que lui-même).
      const device = { actingAs: null } as const;
      const partnersPromise = apiClient
        .get<Partner[]>('/partners', token, device)
        .catch(() => null);
      void apiClient
        .get<MyStoresResponse>('/users/me/stores', token, device)
        .catch(() => null)
        .then((res) => this.warmDeviceStores(res))
        .catch((err: unknown) => console.warn('[cache-warmer] stores failed (ignored):', err));
      const [users, partners, verticales, storiesRes, verifiers] = await Promise.all([
        apiClient.get<User[]>('/users', token, device).catch(() => null),
        partnersPromise,
        apiClient
          .get<
            (Verticale & { deliveryPartners?: DeliveryPartner[] })[]
          >('/configuration/verticales', token, device)
          .catch(() => null),
        // Le Fil : un 403 = le flag est fermé à ce rôle → on VIDE la table
        // (pas d'anneau fantôme après une désactivation). Toute autre erreur
        // (500, timeout, réseau) → `null` = on GARDE le cache, comme les
        // autres référentiels : une requête ratée ne détruit pas la vérité
        // hors ligne ni l'état « vu ».
        // En session PIN on ne touche pas au Fil (`null` = cache intact) : la
        // table porte les intentions du superviseur (« à rappeler », « vu »),
        // qui doivent lui survivre. L'entrée Nouveautés est masquée en PIN.
        isActingAs()
          ? Promise.resolve(null)
          : apiClient
              .get<StoriesResponse>('/stories', token)
              .catch((err: unknown) => (isForbidden(err) ? ('forbidden' as const) : null)),
        // Vérificateurs PIN hors ligne — seule la session d'un superviseur d'un
        // réseau PIN les reçoit ; 403 ailleurs → table vidée (aucun résidu
        // après désactivation). Erreur réseau → on garde le cache.
        partnersPromise.then((partners) =>
          // Inutile d'interroger — et de se faire refuser toutes les 5 min —
          // quand aucun réseau visible n'a activé le PIN.
          partners?.some((p) => isRetail(p))
            ? apiClient
                .get<PinVerifier[]>('/pin/verifiers', token, device)
                .catch((err: unknown) => (isForbidden(err) ? ('forbidden' as const) : null))
            : ('forbidden' as const),
        ),
      ]);
      await this.warmStories(storiesRes);
      await this.warmPinVerifiers(verifiers);
      await db.transaction(
        'rw',
        [db.users, db.partners, db.verticales, db.deliveryPartners],
        async () => {
          if (users) {
            const rows: CachedUserRow[] = users.map((u) => ({
              id: u.id,
              firstName: u.firstName,
              lastName: u.lastName,
              email: u.email,
              role: u.role,
              partnerId: u.partnerId,
              matricule: u.matricule,
              managerId: u.managerId ?? null,
              superviseurId: u.superviseurId ?? null,
              hasPin: u.hasPin === true,
            }));
            await db.users.clear();
            await db.users.bulkAdd(rows);
          }
          if (partners) {
            const rows: CachedPartnerRow[] = partners.map((p) => ({
              id: p.id,
              name: p.name,
              matriculePrefix: p.matriculePrefix,
              contactButtons: p.contactButtons,
              telcoOrderNumber: p.telcoOrderNumber,
            }));
            await db.partners.clear();
            await db.partners.bulkAdd(rows);
          }
          if (verticales) {
            const vRows: CachedVerticaleRow[] = verticales.map((v) => ({
              id: v.id,
              name: v.name,
              slug: v.slug,
              priority: v.priority,
              icon: v.icon ?? null,
              // Ni prix de vente ni rétrocession : rien de financier sur l'appareil.
              arguments: v.arguments,
              active: v.active,
            }));
            await db.verticales.clear();
            await db.verticales.bulkAdd(vRows);

            const dpRows: CachedDeliveryPartnerRow[] = verticales.flatMap((v) =>
              (v.deliveryPartners ?? []).map((dp) => ({
                id: dp.id,
                name: dp.name,
                brand: dp.brand ?? null,
                callcenter: dp.callcenter ?? null,
                mirrorPhone: dp.mirrorPhone ?? null,
                telcoProductScope: dp.telcoProductScope ?? null,
                revealText: dp.revealText,
                verticaleId: dp.verticaleId,
                weight: dp.weight,
                active: dp.active,
                openingStart: dp.openingStart ?? null,
                openingEnd: dp.openingEnd ?? null,
                openingDays:
                  Array.isArray(dp.openingDays) && dp.openingDays.length > 0
                    ? dp.openingDays
                    : [1, 2, 3, 4, 5],
                dailyQuota: dp.dailyQuota ?? null,
                deliveredToday: dp.deliveredToday ?? 0,
              })),
            );
            await db.deliveryPartners.clear();
            await db.deliveryPartners.bulkAdd(dpRows);
          }
        },
      );
    } catch (err) {
      console.warn('[cache-warmer] warm failed (ignored):', err);
    }
  }
}

export const CacheWarmer = new CacheWarmerImpl();

/** L'erreur d'`apiClient` porte le statut HTTP ; 403 = flag fermé, pas une panne. */
function isForbidden(err: unknown): boolean {
  return typeof err === 'object' && err !== null && (err as { status?: unknown }).status === 403;
}
